import Box from '@mui/material/Box';
import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import UniversalMediaUploaderStatus from '../../components/universal-media-uploader-status';
import { useMultiFileUploaderFlag } from '../../components/hooks/use-multi-file-uploader-flag';
import { formatMediaDateTitle } from '../media/metadata-date';
import Textarea from '../../components/form-elements/textarea';
import type { ThumbnailProps } from './media-props';
import Thumbnail from './thumbnail';
import type { MediaType } from './media-field-type';
import { LegacyCaptionField, readOnlyCaptionSx } from './caption-field';

interface Props extends ThumbnailProps {
  url: string
  caption: string
  onCaptionChange?: (newCaption: string) => void
  editing?: boolean
  disabled?: boolean
  /** File size in bytes (used by UniversalMediaUploaderStatus in multi-file mode) */
  fileSize?: number
  /** Display name for the file (preferred over date fallback) */
  fileName?: string
  /** Raw created date string (formatted into display title by this component) */
  createdAt?: string | null
  /** Called when the user clicks the remove/close button in multi-file mode */
  onRemove?: () => void
  /** Click handler for multi-file mode items (opens viewer) */
  onClick?: () => void
  /** Media field type — forwarded to UniversalMediaUploaderStatus for icon selection */
  fileType?: MediaType
  /** Controls how the thumbnail image fits — forwarded to UniversalMediaUploaderStatus in multi-file mode */
  thumbnailStyle?: 'cover' | 'contain'
}

const CaptionedThumbnail: React.FC<Props> = ({
  url,
  mediaID,
  children,
  onDelete,
  handleDeleteRequest,
  caption,
  onCaptionChange,
  editing = false,
  disabled = false,
  selectMedia,
  imgStyles,
  fileSize,
  fileName,
  createdAt,
  onRemove,
  onClick,
  fileType,
  thumbnailStyle,
}: Props) => {
  const [currentCaption, setCurrentCaption] = useState<string>(caption ?? '');
  const isMultiFile = useMultiFileUploaderFlag();

  useEffect(() => {
    setCurrentCaption(caption ?? '');
  }, [caption]);

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentCaption(e.target.value);
    if (onCaptionChange) {
      onCaptionChange(e.target.value);
    }
  };

  const handleCaptionBlur = () => {
    const trimmed = currentCaption.trimEnd();
    setCurrentCaption(trimmed);
    if (onCaptionChange) {
      onCaptionChange(trimmed);
    }
  };

  if (isMultiFile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <UniversalMediaUploaderStatus
          name={fileName ?? formatMediaDateTitle(createdAt)}
          size={fileSize}
          thumbnailURL={url}
          onClose={editing && !disabled ? onRemove : undefined}
          onClick={onClick}
          avatarSize="large"
          fileType={fileType}
          thumbnailStyle={thumbnailStyle}
        />
        {editing && !disabled && (
          <Box sx={{ p: 2, width: '100%' }}>
            <Textarea
              value={currentCaption || ''}
              onChange={handleCaptionChange}
              onBlur={handleCaptionBlur}
              label="Caption"
              size="small"
              minRows={1}
              maxRows={5}
              sx={{ width: '100%', '& .MuiInputLabel-root': { left: '-3px' } }}
              inputProps={{ 'aria-label': 'Caption' }}
            />
          </Box>
        )}
        {!(editing && !disabled) && currentCaption && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" sx={readOnlyCaptionSx}>
              {currentCaption}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* @ts-ignore */}
      <Thumbnail
        url={url}
        mediaID={mediaID}
        altText={caption}
        allowDeleting={!!(onDelete || handleDeleteRequest) && editing && !disabled}
        onDelete={onDelete}
        handleDeleteRequest={handleDeleteRequest}
        selectMedia={selectMedia}
        imgStyles={imgStyles}
      >
        {children}
      </Thumbnail>
      {editing && !disabled && (
        <LegacyCaptionField
          value={currentCaption || ''}
          onChange={handleCaptionChange}
          placeholder="enter a caption"
          variant="outlined"
          size="small"
          multiline
          inputProps={{ 'aria-label': 'Caption' }}
        />
      )}
      {(!editing || disabled) && currentCaption && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {currentCaption}
        </Typography>
      )}
    </Box>
  );
};

export default CaptionedThumbnail;
